import { NavLink } from 'react-router-dom';
import { BookOpen, Activity, FileText, Mail, MessageSquareText, Users } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Segmented control that switches between the Books admin and the
 * Articles admin. Both surfaces share the admin shell; the tabs live
 * just above each page's own sub-header so the navigation context
 * reads "Admin > [Books|Articles] > <page title>".
 *
 * The active tab is resolved by react-router's NavLink `isActive` prop
 * based on the current path. Each tab is a real link so browser
 * navigation / middle-click / open-in-new-tab all work correctly.
 */
export function AdminSectionTabs() {
  return (
    <nav
      aria-label="Admin sections"
      className="inline-flex items-center rounded-full border border-or-slate-200 bg-white p-1"
    >
      <Tab to="/admin" icon={<BookOpen className="h-3.5 w-3.5" aria-hidden />} end>
        Books
      </Tab>
      <Tab to="/admin/articles" icon={<FileText className="h-3.5 w-3.5" aria-hidden />}>
        Articles
      </Tab>
      <Tab to="/admin/reviews" icon={<MessageSquareText className="h-3.5 w-3.5" aria-hidden />}>
        Reviews
      </Tab>
      <Tab to="/admin/affiliates" icon={<Users className="h-3.5 w-3.5" aria-hidden />}>
        Affiliates
      </Tab>
      <Tab to="/admin/subscribers" icon={<Mail className="h-3.5 w-3.5" aria-hidden />}>
        Subscribers
      </Tab>
      <Tab to="/admin/monitoring" icon={<Activity className="h-3.5 w-3.5" aria-hidden />}>
        Monitoring
      </Tab>
    </nav>
  );
}

interface TabProps {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  /** Pass `end` for the Books tab so "/admin/articles" doesn't mark Books active too. */
  end?: boolean;
}

function Tab({ to, icon, children, end }: TabProps) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition-colors',
          isActive
            ? 'bg-or-slate-900 text-white'
            : 'text-or-slate-600 hover:bg-or-slate-100 hover:text-or-slate-800',
        )
      }
    >
      {icon}
      {children}
    </NavLink>
  );
}
